import { useMemo } from 'react';
import { FeatureReadinessNameAllowBookingSite } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/feature_readiness.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import type { BookingSiteSetupReasonCode } from './use-booking-site-setup-reason-action';
import type {
  BookingSiteSetupStatus,
  UseFeatureReadinessBookingSiteSetupStatusArgs,
} from './use-booking-site-setup-status';

export const useFeatureReadinessBookingSiteSetupStatus = ({
  enabled,
  selectedLocationIds,
  selectedOrgId,
  isScheduleDefinitionsEnabled,
}: UseFeatureReadinessBookingSiteSetupStatusArgs): BookingSiteSetupStatus => {
  const { data: featureReadinessData, isLoading: isLoadingFeatureReadiness } =
    SchedulerV3.Queries.useListLocationFeatureReadinessQuery({
      request: {
        orgId: selectedOrgId,
        includeDetails: true,
        names: [FeatureReadinessNameAllowBookingSite],
        filter: 'locationId in (' + selectedLocationIds.join(',') + ')',
        pageSize: selectedLocationIds.length,
      },
      options: {
        enabled,
      },
    });

  const { locationWiseReasonCode, locationWiseIsSetupMap } = useMemo(() => {
    if (!enabled || !featureReadinessData?.locations) {
      return {
        locationWiseReasonCode: {} as Record<string, BookingSiteSetupReasonCode>,
        locationWiseIsSetupMap: {} as Record<string, boolean>,
      };
    }

    const setupMap = selectedLocationIds.reduce((acc, locationId) => {
      acc[locationId] = false;
      return acc;
    }, {} as Record<string, boolean>);

    const reasonCodeMap = {} as Record<string, BookingSiteSetupReasonCode>;

    for (const locationData of featureReadinessData.locations) {
      if (!locationData.locationId || !Object.prototype.hasOwnProperty.call(setupMap, locationData.locationId)) {
        continue;
      }

      const feature = locationData.features?.[0];
      setupMap[locationData.locationId] = Boolean(feature?.enabled);

      if (feature?.reasonCodes && feature.reasonCodes.length > 0) {
        reasonCodeMap[locationData.locationId] = feature.reasonCodes[0] as BookingSiteSetupReasonCode;
      }
    }

    return {
      locationWiseReasonCode: reasonCodeMap,
      locationWiseIsSetupMap: setupMap,
    };
  }, [enabled, featureReadinessData, selectedLocationIds]);

  const hasAnyLocationSetup = enabled && Object.values(locationWiseIsSetupMap).some(Boolean);
  const reasonCode = enabled ? locationWiseReasonCode[selectedLocationIds[0]] : undefined;

  return {
    isLoading: enabled ? isLoadingFeatureReadiness : false,
    hasAnyLocationSetup,
    isScheduleDefinitionsEnabled,
    reasonCode,
    locationWiseIsSetupMap,
    locationWiseReasonCode,
  };
};
